import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  LogOut,
  Loader2,
  RefreshCw,
  Search,
  Download,
  Lock,
  Trash2,
  ShieldCheck,
  Users,
  Copy,
  Calendar,
  Filter,
  MessageCircle,
  Save,
  Plus,
  Link as LinkIcon,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { setCachedWhatsAppUrls } from "@/lib/whatsapp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bancoorp_admin_token";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "—";
  }
};

const csvEscape = (v) => {
  const s = String(v ?? "").replace(/"/g, '""');
  return /[",\n;]/.test(s) ? `"${s}"` : s;
};

// Normalize a RUT to compare duplicates: strip dots/dashes/spaces, lowercase.
const normalizeRut = (rut) =>
  String(rut || "")
    .replace(/[.\-\s]/g, "")
    .toLowerCase();

const PRESETS = [
  { key: "today", label: "Hoy" },
  { key: "yesterday", label: "Ayer" },
  { key: "7d", label: "Últimos 7 días" },
  { key: "30d", label: "Últimos 30 días" },
  { key: "thisMonth", label: "Este mes" },
  { key: "lastMonth", label: "Mes pasado" },
  { key: "all", label: "Todo" },
  { key: "custom", label: "Personalizado" },
];

const startOf = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOf = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};
const toInput = (d) => {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const presetRange = (key) => {
  const now = new Date();
  switch (key) {
    case "today":
      return [startOf(now), endOf(now)];
    case "yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return [startOf(y), endOf(y)];
    }
    case "7d": {
      const s = new Date(now);
      s.setDate(s.getDate() - 6);
      return [startOf(s), endOf(now)];
    }
    case "30d": {
      const s = new Date(now);
      s.setDate(s.getDate() - 29);
      return [startOf(s), endOf(now)];
    }
    case "thisMonth": {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return [startOf(s), endOf(now)];
    }
    case "lastMonth": {
      const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const e = new Date(now.getFullYear(), now.getMonth(), 0);
      return [startOf(s), endOf(e)];
    }
    case "all":
    default:
      return [null, null];
  }
};

function Login({ onAuth }) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    setError("");
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token.trim()}` },
      });
      localStorage.setItem(TOKEN_KEY, token.trim());
      onAuth(token.trim());
    } catch {
      setError("Token inválido. Verifica e inténtalo nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-slate-200" data-testid="admin-login">
        <CardHeader className="text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center mb-2">
            <Lock className="h-5 w-5" />
          </div>
          <CardTitle className="text-xl">Panel administrativo</CardTitle>
          <CardDescription>
            Ingresa el token de acceso para ver las solicitudes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="token" className="text-xs font-semibold text-slate-700">
                Token de acceso
              </Label>
              <Input
                id="token"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Pega aquí tu token"
                autoComplete="off"
                data-testid="admin-token-input"
              />
            </div>
            {error && (
              <div
                className="text-xs rounded-md bg-red-50 border border-red-200 text-red-700 px-3 py-2"
                data-testid="admin-error"
              >
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verificando…
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 mr-2" /> Ingresar
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function WhatsAppSettings({ headers }) {
  const [urls, setUrls] = useState([""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/config/whatsapp`)
      .then((res) => {
        const list = Array.isArray(res.data?.urls) ? res.data.urls : [];
        setUrls(list.length ? list : [""]);
      })
      .catch(() => toast.error("No se pudo cargar la configuración."))
      .finally(() => setLoading(false));
  }, []);

  const updateUrl = (i, v) =>
    setUrls((prev) => prev.map((u, idx) => (idx === i ? v : u)));

  const addUrl = () => setUrls((prev) => [...prev, ""]);

  const removeUrl = (i) =>
    setUrls((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const save = async (e) => {
    e.preventDefault();
    const cleaned = urls.map((u) => u.trim()).filter(Boolean);
    if (!cleaned.length) {
      toast.error("Agrega al menos una URL.");
      return;
    }
    for (const u of cleaned) {
      if (!/^https?:\/\//i.test(u)) {
        toast.error("Cada URL debe comenzar con http:// o https://");
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/config/whatsapp`,
        { urls: cleaned },
        { headers }
      );
      const saved = res.data?.urls || cleaned;
      setUrls(saved);
      setCachedWhatsAppUrls(saved);
      toast.success(
        saved.length > 1
          ? `${saved.length} URLs guardadas. Se alternarán al enviar leads.`
          : "URL guardada."
      );
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        (err.response?.status === 401 ? "Sesión inválida." : "No se pudo guardar.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6" data-testid="wa-settings-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#25D366] text-white flex items-center justify-center">
            <MessageCircle className="h-4 w-4" />
          </div>
          <div>
            <CardTitle>URLs de redirección</CardTitle>
            <CardDescription>
              Pega una o varias URLs completas. Si hay más de una, se alternan
              entre los clientes en orden rotativo.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 flex items-center text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Cargando…
          </div>
        ) : (
          <form onSubmit={save} className="space-y-3">
            {urls.map((u, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-slate-100 text-slate-500 shrink-0 text-xs font-bold">
                  #{i + 1}
                </div>
                <div className="relative flex-1">
                  <LinkIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={u}
                    onChange={(e) => updateUrl(i, e.target.value)}
                    placeholder="https://api.whatsapp.com/send?phone=…&text=…"
                    className="pl-9"
                    data-testid={`wa-url-input-${i}`}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeUrl(i)}
                  disabled={urls.length === 1}
                  className="text-red-600 hover:bg-red-50 h-10 w-10 p-0 shrink-0"
                  aria-label="Eliminar URL"
                  data-testid={`wa-url-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addUrl}
                className="rounded-full"
                data-testid="wa-url-add"
              >
                <Plus className="h-4 w-4 mr-2" />
                Añadir otra URL
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                data-testid="wa-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando…
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" /> Guardar cambios
                  </>
                )}
              </Button>
            </div>

            <p className="text-[11px] text-slate-500 pt-1">
              Puedes pegar cualquier URL (WhatsApp, Telegram, formulario externo,
              etc.). El orden de rotación comienza por la primera URL de la lista.
            </p>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [hideDup, setHideDup] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers });
      setLeads(res.data.leads || []);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sesión expirada. Vuelve a ingresar el token.");
        onLogout();
      } else {
        toast.error("Error al cargar solicitudes.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Mark each lead with isDup flag based on RUT seen earlier in time.
  // Leads come sorted by timestamp desc. To mark first-seen as unique we walk
  // chronologically (asc) and flag subsequent ones with same RUT as duplicates.
  const annotated = useMemo(() => {
    const asc = [...leads].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );
    const seen = new Set();
    const mapDup = new Map();
    for (const l of asc) {
      const k = normalizeRut(l.rut);
      if (k && seen.has(k)) {
        mapDup.set(l.id, true);
      } else {
        if (k) seen.add(k);
        mapDup.set(l.id, false);
      }
    }
    return leads.map((l) => ({ ...l, isDup: !!mapDup.get(l.id) }));
  }, [leads]);

  // Effective date range
  const [rangeStart, rangeEnd] = useMemo(() => {
    if (preset === "custom") {
      const s = from ? startOf(new Date(from)) : null;
      const e = to ? endOf(new Date(to)) : null;
      return [s, e];
    }
    return presetRange(preset);
  }, [preset, from, to]);

  // Apply date range + search filter
  const filtered = useMemo(() => {
    let arr = annotated;
    if (rangeStart) {
      arr = arr.filter((l) => new Date(l.timestamp) >= rangeStart);
    }
    if (rangeEnd) {
      arr = arr.filter((l) => new Date(l.timestamp) <= rangeEnd);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      arr = arr.filter((l) =>
        [l.nombre, l.rut, l.correo, l.telefono, l.monto, l.origen]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    if (hideDup) arr = arr.filter((l) => !l.isDup);
    return arr;
  }, [annotated, rangeStart, rangeEnd, query, hideDup]);

  // Stats over filtered range
  const stats = useMemo(() => {
    const inRange = annotated.filter((l) => {
      if (rangeStart && new Date(l.timestamp) < rangeStart) return false;
      if (rangeEnd && new Date(l.timestamp) > rangeEnd) return false;
      return true;
    });
    const totalReg = inRange.length;
    const uniqueRuts = new Set(
      inRange.map((l) => normalizeRut(l.rut)).filter(Boolean)
    );
    const dup = inRange.filter((l) => l.isDup).length;
    const last = annotated[0];
    return {
      totalReg,
      uniqueClientes: uniqueRuts.size,
      duplicados: dup,
      last,
    };
  }, [annotated, rangeStart, rangeEnd]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key === "custom") {
      const today = new Date();
      const past = new Date();
      past.setDate(past.getDate() - 6);
      if (!from) setFrom(toInput(past));
      if (!to) setTo(toInput(today));
    }
  };

  const exportCSV = () => {
    const cols = [
      "timestamp",
      "nombre",
      "rut",
      "telefono",
      "correo",
      "monto",
      "origen",
      "redirect_url",
      "duplicado",
      "id",
    ];
    const rows = [cols.join(",")].concat(
      filtered.map((l) =>
        cols
          .map((c) =>
            c === "duplicado" ? (l.isDup ? "si" : "no") : csvEscape(l[c])
          )
          .join(",")
      )
    );
    const csv = rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bancoorp-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar esta solicitud?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Solicitud eliminada.");
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
              <Lock className="h-4 w-4" />
            </div>
            <div>
              <div className="font-extrabold text-slate-900 tracking-tight">
                Bancoorp · Admin
              </div>
              <div className="text-xs text-slate-500">
                Solicitudes recibidas
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="rounded-full"
              data-testid="admin-refresh"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="rounded-full text-red-600 hover:bg-red-50"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* WhatsApp settings */}
        <WhatsAppSettings headers={headers} />

        {/* Range filter */}
        <Card className="mb-6">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <Filter className="h-3.5 w-3.5" /> Filtros
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => applyPreset(p.key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-all ${
                    preset === p.key
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-md">
                <div>
                  <Label htmlFor="from" className="text-xs font-semibold text-slate-700">
                    Desde
                  </Label>
                  <Input
                    id="from"
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    data-testid="filter-from"
                  />
                </div>
                <div>
                  <Label htmlFor="to" className="text-xs font-semibold text-slate-700">
                    Hasta
                  </Label>
                  <Input
                    id="to"
                    type="date"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    data-testid="filter-to"
                  />
                </div>
              </div>
            )}
            {(rangeStart || rangeEnd) && (
              <p className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                Rango activo:{" "}
                <strong className="text-slate-700">
                  {rangeStart ? rangeStart.toLocaleDateString("es-CL") : "—"}
                </strong>{" "}
                a{" "}
                <strong className="text-slate-700">
                  {rangeEnd ? rangeEnd.toLocaleDateString("es-CL") : "—"}
                </strong>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
                <Users className="h-3.5 w-3.5" /> Clientes únicos
              </div>
              <div
                className="mt-2 text-3xl font-extrabold text-slate-900"
                data-testid="admin-unique"
              >
                {stats.uniqueClientes}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Cuenta única por RUT en el rango
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Registros en rango
              </div>
              <div
                className="mt-2 text-3xl font-extrabold text-slate-900"
                data-testid="admin-total"
              >
                {stats.totalReg}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Total enviados (incluye duplicados)
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2">
                <Copy className="h-3.5 w-3.5" /> Duplicados
              </div>
              <div className="mt-2 text-3xl font-extrabold text-amber-600">
                {stats.duplicados}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Mismo RUT enviado más de una vez
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Última solicitud
              </div>
              <div className="mt-2 text-sm font-semibold text-slate-900 truncate">
                {stats.last ? fmt(stats.last.timestamp) : "—"}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {stats.last?.nombre || "—"}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle>Solicitudes</CardTitle>
              <CardDescription>
                Datos enviados desde el formulario público.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-slate-600 select-none">
                <input
                  type="checkbox"
                  checked={hideDup}
                  onChange={(e) => setHideDup(e.target.checked)}
                  className="rounded border-slate-300"
                  data-testid="hide-dup-toggle"
                />
                Ocultar duplicados
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar nombre, RUT, correo…"
                  className="pl-9 w-64"
                  data-testid="admin-search"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={exportCSV}
                disabled={!filtered.length}
                className="rounded-full"
                data-testid="admin-export"
              >
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {loading ? (
              <div className="py-16 flex items-center justify-center text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                {leads.length === 0
                  ? "Aún no hay solicitudes."
                  : "Sin resultados para el filtro seleccionado."}
              </div>
            ) : (
              <Table data-testid="admin-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>RUT</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Monto</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead>Redirigido a</TableHead>
                    <TableHead className="text-right">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((l) => (
                    <TableRow
                      key={l.id}
                      className={l.isDup ? "bg-amber-50/50" : ""}
                      data-testid={`lead-row-${l.id}`}
                    >
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        {fmt(l.timestamp)}
                      </TableCell>
                      <TableCell className="font-semibold text-slate-900">
                        <div className="flex items-center gap-2">
                          {l.nombre}
                          {l.isDup && (
                            <span className="text-[10px] uppercase tracking-wider rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-bold">
                              Duplicado
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-700">{l.rut}</TableCell>
                      <TableCell className="text-slate-700 whitespace-nowrap">
                        <a
                          href={`tel:${l.telefono.replace(/\s/g, "")}`}
                          className="hover:underline"
                        >
                          {l.telefono}
                        </a>
                      </TableCell>
                      <TableCell className="text-slate-700">
                        <a
                          href={`mailto:${l.correo}`}
                          className="hover:underline"
                        >
                          {l.correo}
                        </a>
                      </TableCell>
                      <TableCell className="text-slate-900 font-semibold">
                        {l.monto}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs rounded-full bg-slate-100 px-2 py-1 text-slate-600">
                          {l.origen || "landing"}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {l.redirect_url ? (
                          <a
                            href={l.redirect_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 truncate max-w-[180px]"
                            title={l.redirect_url}
                          >
                            <ExternalLink className="h-3 w-3 shrink-0" />
                            <span className="truncate">{l.redirect_url.replace(/^https?:\/\//, "")}</span>
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(l.id)}
                          className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                          aria-label="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [verifying, setVerifying] = useState(!!token);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    axios
      .get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => setVerifying(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setVerifying(false);
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Verificando sesión…
      </div>
    );
  }

  if (!token) return <Login onAuth={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
